/**
 * Email Verification Banner Component
 * Warning banner for unverified users in Dashboard
 */

import React, { useState, useEffect } from 'react'
import { useEmailVerification } from '../../hooks/useEmailVerification'
import toast from 'react-hot-toast'
import type { ResendEmailErrorResponse } from '../../types/email-verification'

interface EmailVerificationBannerProps {
  email: string
  onDismiss?: () => void
}

export function EmailVerificationBanner({ email, onDismiss }: EmailVerificationBannerProps) {
  const { resend } = useEmailVerification(null)
  const [countdown, setCountdown] = useState(0)
  const [isDismissed, setIsDismissed] = useState(false)

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [countdown])

  // Handle resend button click
  const handleResend = () => {
    resend.mutate(undefined, {
      onSuccess: () => {
        toast.success('驗證郵件已重新發送,請查收')
      },
      onError: (error: ResendEmailErrorResponse) => {
        if (error.code === 'TOO_FREQUENT') {
          const retryAfter = error.retryAfter || 60
          setCountdown(retryAfter)
          toast.error(`請稍後再試 (剩餘 ${retryAfter} 秒)`)
        } else if (error.code === 'ALREADY_VERIFIED') {
          toast.success('您的 Email 已驗證')
        } else {
          toast.error('發送失敗,請稍後再試')
        }
      },
    })
  }

  // Handle dismiss
  const handleDismiss = () => {
    setIsDismissed(true)
    if (onDismiss) {
      onDismiss()
    }
  }

  // Don't render if dismissed
  if (isDismissed) {
    return null
  }

  const isDisabled = resend.isPending || countdown > 0

  return (
    <div style={styles.banner}>
      <div style={styles.content}>
        {/* Alert Icon */}
        <div style={styles.iconWrapper}>
          <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Message */}
        <div style={styles.message}>
          <p style={styles.title}>請驗證您的 Email</p>
          <p style={styles.description}>
            我們已發送驗證郵件至 <strong>{email}</strong>,請查收並完成驗證
          </p>
        </div>

        {/* Actions */}
        <div style={styles.actions}>
          <button
            onClick={handleResend}
            disabled={isDisabled}
            style={{
              ...styles.resendButton,
              ...(isDisabled ? styles.resendButtonDisabled : {}),
            }}
          >
            {resend.isPending && <span style={styles.spinner}></span>}
            {countdown > 0 ? `重新發送 (${countdown}秒)` : '重新發送驗證郵件'}
          </button>
        </div>
      </div>

      {/* Dismiss Button */}
      <button onClick={handleDismiss} style={styles.dismissButton} aria-label="關閉提醒">
        <svg style={styles.dismissIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  banner: {
    backgroundColor: '#fef3c7', // yellow-100
    border: '1px solid #fbbf24', // yellow-400
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    position: 'relative',
  },
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    flex: 1,
    flexWrap: 'wrap', // T034: Allow wrapping on mobile
  },
  iconWrapper: {
    flexShrink: 0,
    width: '24px',
    height: '24px',
    color: '#f59e0b', // yellow-500
  },
  icon: {
    width: '24px',
    height: '24px',
  },
  message: {
    flex: 1,
  },
  title: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#92400e', // yellow-900
    marginBottom: '4px',
  },
  description: {
    fontSize: '14px',
    color: '#78350f', // yellow-900
    lineHeight: '1.5',
  },
  actions: {
    flexShrink: 0,
    minWidth: '200px', // T034: Ensure button has minimum width on mobile
  },
  resendButton: {
    padding: '8px 16px',
    backgroundColor: '#f59e0b', // yellow-500
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    whiteSpace: 'nowrap',
  },
  resendButtonDisabled: {
    backgroundColor: '#d97706', // yellow-600 (darker when disabled)
    cursor: 'not-allowed',
    opacity: 0.7,
  },
  dismissButton: {
    flexShrink: 0,
    width: '24px',
    height: '24px',
    padding: 0,
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#92400e', // yellow-900
    transition: 'color 0.2s',
  },
  dismissIcon: {
    width: '20px',
    height: '20px',
  },
  spinner: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
}
